import React, {useEffect} from 'react';
import styled from 'styled-components/native';
import Icon from '../icon';
import MyPressable from '../myPressable';
import TextGeneric from '../textGeneric';
import Animated, {
  cancelAnimation,
  Easing,
  FadeInLeft,
  FadeOutRight,
  useAnimatedStyle,
  useSharedValue,
  withRepeat,
  withTiming,
} from 'react-native-reanimated';
import {CircleItemProps} from '../../interfaces/components/circleItem';
import {colors} from '../../theme/colors';
import {tr} from '../../translations';
import {useTheme} from 'styled-components';
import {Theme} from '../../theme/lightTheme';
import {Platform} from 'react-native';
import {getByScreenSize} from '../../utils/responsive';

const Container = styled(MyPressable)`
  width: 100%;
`;
const LabelContainer = styled.View<{isProgress?: boolean}>`
  width: 100%;
  padding: ${({isProgress}) => (isProgress ? 10 : 0)}%;
  aspect-ratio: 1;
  position: absolute;
  top: 0;
  left: 0;
  align-items: center;
  justify-content: center;
  z-index: 2;
`;
const Title = styled(TextGeneric)`
  font-size: ${({theme}) =>
    theme.text.s8 * (Platform.OS === 'android' ? 0.9 : 1)}px;
  color: ${({theme, isError}) => (isError ? theme.text.error : colors.warning)};
  letter-spacing: 1px;
  text-align: center;
  width: 85%;
  padding-top: ${({isSeeAllVisible}) => (isSeeAllVisible ? 10 : 4)}%;
  padding-bottom: ${getByScreenSize('4%', '2%', '1%')};
`;
const Label = styled(TextGeneric)`
  font-size: ${({theme}) =>
    theme.text.s8 * (Platform.OS === 'android' ? 0.9 : 1)}px;
  line-height: ${({theme}) =>
    theme.text.s8 * (Platform.OS === 'android' ? 0.9 : 1) * 1.2}px;

  color: ${({theme}) => theme.text.light};
  text-align: center;
  width: 85%;
  letter-spacing: ${({isProgress}) => (isProgress ? 0 : 0.5)}px;
`;
const SeeAllContainer = styled.TouchableOpacity`
  width: 60%;
  aspect-ratio: 4;
  border-radius: 100px;
  overflow: hidden;
  margin-top: 6%;
`;
const SeeAllAnimatedContainer = styled(Animated.View)`
  width: 100%;
  height: 100%;
`;
const SeeAllButtonIconContainer = styled.View`
  width: 100%;
  aspect-ratio: 4;
  position: absolute;
  top: 0;
  left: 0;
`;
const SeeAllContentContainer = styled.View`
  flex-direction: row;
  align-items: center;
  width: 100%;
  height: 100%;
`;
const ArrowIconContainer = styled.View`
  padding-horizontal: 5%;
  position: absolute;
  height: 100%;
  right: 5%;
`;
const SeeAllText = styled(TextGeneric)`
  font-size: ${({theme}) => theme.text.s9}px;
  line-height: ${({theme}) => theme.text.s9 * 1.4}px;
  color: ${({theme}) => theme.text.light};
  text-align: center;
  flex: 1;
  letter-spacing: 1px;
`;
/**
 * Circle Item component with rotating animation when loading.
 *
 * @param {CircleItemProps} props - The CircleItem component props.
 * @returns {React.ReactElement} The rendered CircleItem component.
 */
const CircleItem: React.FC<CircleItemProps> = ({
  style = {},
  title,
  label,
  isLoading,
  isProgress,
  isSeeAllVisible,
  onSeeAllPress,
  onPress,
  isError,
}: CircleItemProps): React.ReactElement => {
  const theme = useTheme() as Theme;
  const rotation = useSharedValue(isProgress ? 0 : 360);

  useEffect(() => {
    if (isLoading) {
      rotation.value = withRepeat(
        withTiming(isProgress ? 360 : 0, {
          duration: isProgress ? 4000 : 2000,
          easing: Easing.linear,
        }),
        -1,
        false,
      );
    } else {
      rotation.value = withTiming(isProgress ? 0 : 360, {
        duration: isProgress ? 1500 : 1000,
        easing: Easing.out(Easing.cubic),
      });
    }

    return () => cancelAnimation(rotation);
  }, [isLoading, rotation, isProgress]);

  const animatedStyle = useAnimatedStyle(() => ({
    transform: [{rotate: `${rotation.value}deg`}],
  }));

  return (
    <Container
      secondaryRipple
      onPress={onPress}
      borderRadius={300}
      disabledNoOpacity={isError || isLoading || !onPress}
      containerStyle={{
        width: '100%',
        aspectRatio: 1,
        ...style,
      }}>
      <Animated.View style={[animatedStyle]}>
        <Icon
          type={'SVG'}
          name={isProgress ? 'circleProgress' : 'circle'}
          color={theme.secondary + 'DD'}
          size={'100%'}
        />
      </Animated.View>
      <LabelContainer isProgress={isProgress}>
        {!!title && (
          <Title isError={isError} isSeeAllVisible={isSeeAllVisible} bold>
            {title || ''}
          </Title>
        )}
        {!!label && (
          <Label isProgress={isProgress} semiBold>
            {label || ''}
          </Label>
        )}
        {isProgress &&
          (isSeeAllVisible || isError) &&
          (!isLoading || isError) && (
            <SeeAllContainer onPress={onSeeAllPress}>
              <SeeAllAnimatedContainer
                entering={FadeInLeft}
                exiting={FadeOutRight}>
                <SeeAllButtonIconContainer>
                  <Icon
                    type={'SVG'}
                    name={'seeAllButton'}
                    size={'100%'}
                    color={theme.primary + '88'}
                  />
                </SeeAllButtonIconContainer>
                <SeeAllContentContainer>
                  <SeeAllText>
                    {isError ? tr('app.tryAgain') : tr('latest.seeAll')}
                  </SeeAllText>
                  <ArrowIconContainer>
                    <Icon
                      type={'MaterialIcons'}
                      name={isError ? 'refresh' : 'arrow-forward-ios'}
                      size={theme.text.s6}
                      color={theme.text.light}
                    />
                  </ArrowIconContainer>
                </SeeAllContentContainer>
              </SeeAllAnimatedContainer>
            </SeeAllContainer>
          )}
      </LabelContainer>
    </Container>
  );
};

export default CircleItem;
